import { Fragment, useMemo, useState } from 'react';

import { type ColumnDef } from '@tanstack/react-table';

import { Banner, Text } from '@spotify-internal/encore-web';
import {
  useLabelsForResource,
  usePageContentTopOffset,
} from '@spotify/confidence-common-react';
import {
  ConfirmDialog,
  FiltersProvider,
  ListEmptyState,
} from '@spotify/confidence-core-react';
import {
  DataTableViewport,
  useDataTableColumnVisibility,
} from '@spotify/confidence-resource-data-table-react';

import {
  findCollectionEntryExtension,
  getExtensionDisplayName,
  useCollectionEntryExtensions,
} from '../../extensions';
import { useCollectionEntries, useDeleteCollectionEntry } from '../../hooks';
import { FavoritesProvider } from '../FavoritesProvider';
import { CollectionEntriesTable } from './CollectionEntriesTable';
import {
  type CollectionEntryTableRow,
  type EntryToRemove,
  createActionsColumn,
  createAddedColumn,
  createExtensionColumn,
  createFavoriteColumn,
  createLabelsColumn,
  createNameColumn,
  createOwnerColumn,
  createTypeColumn,
} from './collectionEntryColumns';
import { useCollectionEntriesFilter } from './useCollectionEntriesFilter';

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
};

const EntriesListContent = ({
  collectionName,
  isFavorites,
}: CollectionEntriesListProps) => {
  const extensions = useCollectionEntryExtensions();
  const [entryToRemove, setEntryToRemove] = useState<EntryToRemove | null>(
    null,
  );

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const {
    filteredEntries,
    searchQuery,
    setQuery,
    typeFilter,
    availableTypes,
    selectType,
    activeExtension,
    hasActiveFilters,
  } = useCollectionEntriesFilter(collectionEntries, extensions);

  const { labelDefinitions } = useLabelsForResource({});
  const { deleteCollectionEntry } = useDeleteCollectionEntry();

  const rows = useMemo<CollectionEntryTableRow[]>(
    () =>
      filteredEntries.map(entry => {
        const extension = findCollectionEntryExtension(extensions, entry);
        const resource = entry.resource ?? undefined;
        const displayName = resource
          ? getExtensionDisplayName(extension, resource, entry)
          : entry.name;
        const resourceName =
          resource && 'name' in resource ? String(resource.name) : entry.name;
        return {
          name: resourceName,
          displayName,
          entry,
          extension,
          resource,
        };
      }),
    [filteredEntries, extensions],
  );

  const extensionColumns = activeExtension?.columns;

  const columns = useMemo<
    ColumnDef<CollectionEntryTableRow, CollectionEntryTableRow>[]
  >(
    () => [
      createFavoriteColumn(),
      createNameColumn(),
      createTypeColumn(),
      ...(extensionColumns?.map(createExtensionColumn) ?? []),
      createOwnerColumn(),
      createLabelsColumn(labelDefinitions),
      createAddedColumn(),
      createActionsColumn(setEntryToRemove),
    ],
    [extensionColumns, labelDefinitions],
  );

  const { columnVisibility, onColumnVisibilityChange } =
    useDataTableColumnVisibility({
      storageKey: 'collection-entries-columns',
    });

  const topOffset = usePageContentTopOffset();

  if (isEmpty && !hasActiveFilters) {
    return (
      <ListEmptyState
        title={isFavorites ? 'No favorites yet' : 'No items yet'}
        description={
          isFavorites
            ? 'Bookmark a resource to add it to your favorites.'
            : 'Add resources to this collection from their detail pages.'
        }
      />
    );
  }

  return (
    <Fragment>
      {error && !isEmpty && (
        <Banner colorSet="negative" contextual>
          {error.message}
        </Banner>
      )}
      <DataTableViewport topOffset={topOffset}>
        {(scrollRef, scrollElement) => (
          <CollectionEntriesTable
            columns={columns}
            rows={rows}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            scrollRef={scrollRef}
            scrollElement={scrollElement}
            isLoading={isLoading}
            hasEntries={Boolean(collectionEntries?.length)}
            hasNextPage={hasNextPage}
            hasActiveFilters={hasActiveFilters}
            error={error}
            onLoadMore={nextPage}
            searchQuery={searchQuery}
            onSearchChange={setQuery}
            typeFilter={typeFilter}
            availableTypes={availableTypes}
            onTypeSelect={selectType}
          />
        )}
      </DataTableViewport>
      {entryToRemove && (
        <ConfirmDialog
          title="Remove from collection"
          action="Remove"
          onClose={() => setEntryToRemove(null)}
          onConfirm={() => deleteCollectionEntry(entryToRemove.name)}
         data-track-source-anchor="0b5aa3a253;entries-list-content-confirm-dialog">
          <Text variant="bodyMedium">
            Are you sure you want to remove{' '}
            <strong>{entryToRemove.displayName}</strong> from this collection?
          </Text>
        </ConfirmDialog>
      )}
    </Fragment>
  );
};

export const CollectionEntriesList = (props: CollectionEntriesListProps) => (
  <FavoritesProvider enabled>
    <FiltersProvider
      searchField="displayName"
      persistence="session"
      disableQueryParams
    >
      <EntriesListContent {...props} />
    </FiltersProvider>
  </FavoritesProvider>
);
